'use client';

import clsx from 'clsx';
import Icon from '@/components/icon/Icon';
import { navPanelId, navTriggerId } from '../nav-data';
import { useKeyboardActivation } from '../hooks/useKeyboardActivation';

type NavTriggerProps = {
  title: string;
  /** Identity of the group, unique even if two groups share a title. */
  groupKey: string;
  isOpen: boolean;
  onToggle: () => void;
  /** Receives the keys the trigger itself does not act on (arrow navigation). */
  onKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
};

/**
 * Top-level mega-menu trigger.
 *
 * A button rather than a link: the four panel groups have no page of their own
 * in Strapi (`slug` is null), and a control whose job is to expand a panel is a
 * disclosure. As a button it is keyboard reachable, announces its state through
 * `aria-expanded`, and names the panel it owns through `aria-controls` — none
 * of which the `<span>` this replaces could do.
 *
 * Enter and Space are handled explicitly (see `useKeyboardActivation`) so that
 * opening the panel from the keyboard does not depend on the browser's native
 * button activation.
 */
export const NavTrigger = ({
  title,
  groupKey,
  isOpen,
  onToggle,
  onKeyDown,
}: NavTriggerProps) => {
  const activation = useKeyboardActivation(onToggle);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    activation.onKeyDown(event);
    if (!event.defaultPrevented) onKeyDown?.(event);
  };

  return (
    <button
      type="button"
      id={navTriggerId(groupKey)}
      data-dropdown-trigger
      aria-expanded={isOpen}
      aria-controls={navPanelId(groupKey)}
      aria-haspopup="true"
      onClick={activation.onClick}
      onKeyDown={handleKeyDown}
      className={clsx(
        'flex cursor-pointer select-none items-center gap-1 rounded p-1.5 font-[450] text-brand-700 hover:bg-neutral-25 hover:text-gray-700',
        isOpen && '!bg-neutral-25 !text-gray-700',
      )}
    >
      {title}
      <Icon name="chevron-down" size={18} />
    </button>
  );
};

export default NavTrigger;
